import Phaser from 'phaser';
import type { MorseSymbol } from '@/utils/morse';
import { loadSettings } from '@/config/settings';

export type InputCallback = (symbol: MorseSymbol, time: number) => void;

export class InputManager {
  private scene: Phaser.Scene;
  private dotKey: Phaser.Input.Keyboard.Key | null = null;
  private dashKey: Phaser.Input.Keyboard.Key | null = null;
  private callback: InputCallback | null = null;
  private enabled: boolean = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.setupKeyboard();
    this.setupMouse();
  }

  private setupKeyboard(): void {
    const settings = loadSettings();
    const keyboard = this.scene.input.keyboard;

    if (!keyboard) return;

    // Get key codes from settings
    const dotKeyCode = Phaser.Input.Keyboard.KeyCodes[settings.dotKey.toUpperCase() as keyof typeof Phaser.Input.Keyboard.KeyCodes];
    const dashKeyCode = Phaser.Input.Keyboard.KeyCodes[settings.dashKey.toUpperCase() as keyof typeof Phaser.Input.Keyboard.KeyCodes];

    if (dotKeyCode) {
      this.dotKey = keyboard.addKey(dotKeyCode);
      this.dotKey.on('down', () => this.handleInput('.'));
    }

    if (dashKeyCode) {
      this.dashKey = keyboard.addKey(dashKeyCode);
      this.dashKey.on('down', () => this.handleInput('-'));
    }
  }

  private setupMouse(): void {
    // Left click = dot, Right click = dash
    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.leftButtonDown()) {
        this.handleInput('.');
      } else if (pointer.rightButtonDown()) {
        this.handleInput('-');
      }
    });

    // Disable context menu for right-click
    this.scene.game.canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });
  }

  private handleInput(symbol: MorseSymbol): void {
    if (!this.enabled || !this.callback) return;

    const time = this.scene.time.now;
    this.callback(symbol, time);
  }

  /**
   * Set the callback for input events
   */
  onInput(callback: InputCallback): void {
    this.callback = callback;
  }

  /**
   * Enable input handling
   */
  enable(): void {
    this.enabled = true;
  }

  /**
   * Disable input handling
   */
  disable(): void {
    this.enabled = false;
  }

  /**
   * Check if input is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Update key bindings (call when settings change)
   */
  updateBindings(): void {
    // Remove old keys
    if (this.dotKey) {
      this.dotKey.removeAllListeners();
      this.scene.input.keyboard?.removeKey(this.dotKey.keyCode);
    }
    if (this.dashKey) {
      this.dashKey.removeAllListeners();
      this.scene.input.keyboard?.removeKey(this.dashKey.keyCode);
    }

    // Set up new keys
    this.setupKeyboard();
  }

  /**
   * Get current key bindings for display
   */
  getBindings(): { dot: string; dash: string } {
    const settings = loadSettings();
    return {
      dot: settings.dotKey,
      dash: settings.dashKey,
    };
  }

  /**
   * Destroy the input manager
   */
  destroy(): void {
    this.disable();
    this.callback = null;

    if (this.dotKey) {
      this.dotKey.removeAllListeners();
    }
    if (this.dashKey) {
      this.dashKey.removeAllListeners();
    }

    this.scene.input.removeAllListeners('pointerdown');
  }
}
